import React from "react";
import PropTypes from "prop-types";
import { getValueFormatter, getMinMaxRange } from "../utils/formatting";
import { formatterForDataCadence, defaultActions, defaultColors, } from "../utils/visualization";
import { exportToCsv, downloadImage } from "../utils/exporters";
import { toCsvDateFormat, toDate } from "../utils/date";
import { TimeRangeType } from "../utils/propTypes";
import { Message, ChartContainer as DefaultChartContainer, useTectonicContext, } from "../components";
import { validIntervals, intervalToLabel, } from "../utils/intervals";
import { IconAreaChart, IconBarChart, IconLineChart, } from "../components/Icons";
import { Area, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, } from "recharts";
const defaultChartTypes = [
    {
        label: "Line",
        value: "line",
        icon: IconLineChart,
    },
    {
        label: "Bar",
        value: "bar",
        icon: IconBarChart,
    },
    {
        label: "Area",
        value: "area",
        icon: IconAreaChart,
    },
];
export const SeriesChart = ({ exportFilename = "export.csv", valueField = "value", valueFieldLabel = "Value", confidenceField = "confidence", confidenceLabel = "Confidence", confidenceColor = "#f57f7f", height = 400, data = [], status = { success: true }, chartType: propsChartType = "line", chartContainer: ChartContainer = DefaultChartContainer, enabledControls = ["intervals", "chartTypes", "actions"], axisColor = "#363B3D", valueFormatter, labelFormatter, title, titleAlign, disableDot, onIntervalChange, tickFormatter, interval, timeRange, color, annotations = [], }) => {
    const ctx = useTectonicContext();
    const _color = color || (ctx === null || ctx === void 0 ? void 0 : ctx.primaryColor) || defaultColors[0];
    const [chartType, setChartType] = React.useState(propsChartType || "line");
    const svgChartRef = React.useRef(null);
    let ChartGraph;
    React.useEffect(() => {
        setChartType(propsChartType);
    }, [propsChartType]);
    if (chartType == "area") {
        ChartGraph = Area;
    }
    else if (chartType === "bar") {
        ChartGraph = Bar;
    }
    else {
        ChartGraph = Line;
    }
    const defaultTickFormatter = formatterForDataCadence(data);
    const noData = !data || data.length < 2;
    const intervals = validIntervals(toDate(timeRange === null || timeRange === void 0 ? void 0 : timeRange.from), toDate(timeRange === null || timeRange === void 0 ? void 0 : timeRange.to)) || [];
    const handleDownloadImage = async (ref) => {
        if (ref && ref.container) {
            let svg = ref.container.children[0];
            await downloadImage(svg, ref.container.clientWidth, ref.container.clientHeight, "chart.png");
        }
    };
    function handleAction(action) {
        if (action === "download-image") {
            handleDownloadImage(svgChartRef.current);
        }
        else if (action === "export-data") {
            exportToCsv(["Date", "Value"], data.map((row) => [
                `"${toCsvDateFormat(new Date(row.timestamp))}"`,
                row.value,
            ]), exportFilename);
        }
    }
    const _valueFormatter = valueFormatter || getValueFormatter(getMinMaxRange(data, valueField));
    const hasConfidence = data.some((row) => row[confidenceField]);
    return (React.createElement(ChartContainer, { title: title, height: height, titleAlign: titleAlign, enabledControls: noData ? [] : enabledControls, intervals: intervals.map((interval) => {
            return {
                label: intervalToLabel(interval),
                value: interval,
            };
        }), activeChartType: chartType, activeInterval: interval, chartTypes: defaultChartTypes, actions: defaultActions, onChartTypeChange: (chartType) => setChartType(chartType), onActionChange: handleAction, onIntervalChange: onIntervalChange },
        status.success && noData && (React.createElement(Message, null, "No data available for this time period")),
        status.loading && React.createElement(Message, null, "Loading..."),
        status.error && React.createElement(Message, { error: true }, status.error.message),
        React.createElement(ResponsiveContainer, { key: `${chartType}-${status.success}-${data.length}` },
            React.createElement(ComposedChart, { ref: svgChartRef, data: data, margin: {
                    top: 6,
                    right: 6,
                    left: 6,
                    bottom: 6,
                } },
                React.createElement(Tooltip, { formatter: labelFormatter ? labelFormatter : _valueFormatter, labelFormatter: defaultTickFormatter }),
                hasConfidence && chartType !== "bar" && (React.createElement(Area, { type: "monotoneX", dataKey: confidenceField, name: confidenceLabel, stroke: confidenceColor, fill: confidenceColor, fillOpacity: 0.3, strokeOpacity: 0.2 })),
                React.createElement(ChartGraph, Object.assign({ type: "monotoneX", dataKey: valueField, name: valueFieldLabel, stroke: _color, strokeWidth: 2, fill: ["bar", "area"].includes(chartType) ? `${_color}` : undefined, fillOpacity: 0.3, opacity: 1 }, (["line", "area"].includes(chartType)
                    ? {
                        activeDot: disableDot ? { r: 0 } : { r: 6 },
                    }
                    : {}), { dot: false })),
                React.createElement(XAxis, { dataKey: "timestamp", name: "Time", tickFormatter: tickFormatter || defaultTickFormatter, tick: { fill: axisColor, fontSize: "13" }, tickLine: { stroke: axisColor }, axisLine: { stroke: axisColor }, tickMargin: 5, padding: { left: 20, right: 9 } }),
                React.createElement(YAxis, { tickFormatter: _valueFormatter, tick: { fill: axisColor, fontSize: "13" }, tickLine: { fill: axisColor }, tickMargin: 5, padding: { bottom: 10, top: 10 }, mirror: true }),
                annotations.map((annotation) => {
                    return (React.createElement(ReferenceLine, { x: annotation.timestamp.valueOf(), stroke: annotation.color, label: annotation.label }));
                })))));
};
SeriesChart.propTypes = {
    height: PropTypes.number,
    interval: PropTypes.string,
    title: PropTypes.node,
    valueFormatter: PropTypes.func,
    labelFormatter: PropTypes.func,
    confidenceLabel: PropTypes.string,
    confidenceColor: PropTypes.string,
    confidenceField: PropTypes.string,
    valueField: PropTypes.string,
    valueFieldLabel: PropTypes.string,
    chartType: PropTypes.oneOf(["line", "bar", "area"]),
    status: PropTypes.object,
    onIntervalChange: PropTypes.func,
    timeRange: TimeRangeType,
    enabledControls: PropTypes.arrayOf(PropTypes.oneOf(["intervals", "chartTypes", "actions"])),
    data: PropTypes.array,
    color: PropTypes.string,
    axisColor: PropTypes.string,
    legend: PropTypes.bool,
    disableDot: PropTypes.bool,
    chartContainer: PropTypes.elementType,
    exportFilename: PropTypes.string,
};
//# sourceMappingURL=SeriesChart.js.map